import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { UserPreferences, DemoRequest, ContactForm, SupportForm } from '@/types'
import { validateEmail } from '@/utils/helpers'
import { emailService } from '@/services/emailService'

interface UserState {
  isAuthenticated: boolean
  userPreferences: UserPreferences
  demoRequest: DemoRequest
  contactForm: ContactForm
  supportForm: SupportForm
}

interface UserActions {
  updatePreferences: (preferences: Partial<UserPreferences>) => void
  loadPreferences: () => void
  updateDemoRequest: (request: Partial<DemoRequest>) => void
  resetDemoRequest: () => void
  submitDemoRequest: () => Promise<boolean>
  updateContactForm: (form: Partial<ContactForm>) => void
  resetContactForm: () => void
  submitContactForm: () => Promise<boolean>
  updateSupportForm: (form: Partial<SupportForm>) => void
  resetSupportForm: () => void
  submitSupportForm: () => Promise<boolean>
  setLanguage: (language: 'en' | 'es' | 'fr') => void
  setTheme: (theme: 'light' | 'dark') => void
  toggleNotifications: () => void
}

interface UserGetters {
  currentLanguage: string
  currentTheme: string
  isDemoFormValid: () => boolean
  isContactFormValid: () => boolean
}

type UserStore = UserState & UserActions & UserGetters

const initialState: UserState = {
  isAuthenticated: false,
  userPreferences: {
    language: 'en',
    theme: 'light',
    notifications: true
  },
  demoRequest: {
    name: '',
    email: '',
    organization: '',
    service: '',
    phone: '',
    message: ''
  },
  contactForm: {
    name: '',
    email: '',
    organization: '',
    phone: '',
    subject: '',
    message: ''
  },
  supportForm: {
    name: '',
    email: '',
    organization: '',
    clientName: '',
    phone: '',
    priority: 'medium' as const,
    category: 'general' as const,
    subject: '',
    message: ''
  }
}

export const useUserStore = create<UserStore>()(
  persist(
    (set, get) => ({
      ...initialState,

      // Getters
      get currentLanguage() {
        return get().userPreferences.language
      },

      get currentTheme() {
        return get().userPreferences.theme
      },

      isDemoFormValid: () => {
        const { name, email, organization, service, message } = get().demoRequest
        const trimmedName = name?.trim()
        const trimmedEmail = email?.trim()
        const trimmedOrg = organization?.trim()
        const trimmedService = service?.trim()
        const trimmedMessage = message?.trim()
        
        const isValid = !!(
          trimmedName && 
          trimmedEmail && 
          validateEmail(trimmedEmail) &&
          trimmedOrg && 
          trimmedService && 
          trimmedMessage
        )
        
        
        return isValid
      },

      isContactFormValid: () => {
        const { name, email, subject, message } = get().contactForm
        return !!(
          name?.trim() && 
          email?.trim() && 
          validateEmail(email.trim()) &&
          subject?.trim() && 
          message?.trim()
        )
      },

      // Actions
      updatePreferences: (preferences) => {
        set((state) => ({
          userPreferences: { ...state.userPreferences, ...preferences }
        }))
      },

      loadPreferences: () => {
        // This is handled by the persist middleware
        // Just a placeholder for compatibility
      },

      updateDemoRequest: (request) => {
        set((state) => ({
          demoRequest: { ...state.demoRequest, ...request }
        }))
      },

      resetDemoRequest: () => {
        set({ demoRequest: initialState.demoRequest })
      },

      submitDemoRequest: async () => {
        try {
          const { demoRequest } = get()
          
          // Send to email service
          const result = await emailService.sendDemoRequest({
            name: demoRequest.name,
            email: demoRequest.email,
            organization: demoRequest.organization,
            phone: demoRequest.phone,
            service: demoRequest.service,
            message: demoRequest.message
          })
          
          if (result.success) {
            get().resetDemoRequest()
            return true
          } else {
            console.error('Failed to send demo request:', result.message)
            return false
          }
        } catch (error) {
          console.error('Failed to submit demo request:', error)
          return false
        }
      },

      updateContactForm: (form) => {
        set((state) => ({
          contactForm: { ...state.contactForm, ...form }
        }))
      },

      resetContactForm: () => {
        set({ contactForm: initialState.contactForm })
      },

      submitContactForm: async () => {
        try {
          const { contactForm } = get()
          
          // Send to email service
          const result = await emailService.sendContactForm({
            name: contactForm.name,
            email: contactForm.email,
            company: contactForm.organization,
            phone: contactForm.phone,
            subject: contactForm.subject,
            message: contactForm.message
          })
          
          if (result.success) {
            get().resetContactForm()
            return true
          } else {
            console.error('Failed to send contact form:', result.message)
            return false
          }
        } catch (error) {
          console.error('Failed to submit contact form:', error)
          return false
        }
      },

      updateSupportForm: (form) => {
        set((state) => ({
          supportForm: { ...state.supportForm, ...form }
        }))
      },

      resetSupportForm: () => {
        set({ supportForm: initialState.supportForm })
      },

      submitSupportForm: async () => {
        try {
          const { supportForm } = get()
          
          // Use dedicated support form method
          const result = await emailService.sendSupportForm({
            name: supportForm.name,
            email: supportForm.email,
            organization: supportForm.organization,
            clientName: supportForm.clientName,
            phone: supportForm.phone,
            priority: supportForm.priority,
            category: supportForm.category,
            subject: supportForm.subject,
            message: supportForm.message
          })
          
          if (result.success) {
            get().resetSupportForm()
            return true
          } else {
            console.error('Failed to send support form:', result.error)
            return false
          }
        } catch (error) {
          console.error('Failed to submit support form:', error)
          return false
        }
      },

      setLanguage: (language) => {
        get().updatePreferences({ language })
      },

      setTheme: (theme) => {
        get().updatePreferences({ theme })
      },

      toggleNotifications: () => {
        const { userPreferences } = get()
        get().updatePreferences({ notifications: !userPreferences.notifications })
      }
    }),
    {
      name: 'user-store',
      partialize: (state) => ({ userPreferences: state.userPreferences })
    }
  )
)


